import React, { FC, useMemo } from 'react'
import { graphql, usePaginationFragment } from 'react-relay'

import {
  Stack,
  Text,
  HorizontalLine,
  Button,
  CardGrid,
  useBreakpoint,
  GuildCard,
} from '@components'

import { GuildNetworksSection_guild$key } from './__generated__/GuildNetworksSection_guild.graphql'

const INITIAL_COUNTS = {
  0: 1,
  1: 1,
  2: 2,
  3: 3,
  4: 3,
}

export const GuildNetworksSection: FC<{
  guild: GuildNetworksSection_guild$key
}> = ({ guild }) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment GuildNetworksSection_guild on Guild
      @refetchable(queryName: "GuildNetworksSectionPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 12 }
        cursor: { type: "Cursor" }
      ) {
        networks: rankedNetworks(
          membershipStatus: ACCEPTED
          first: $count
          after: $cursor
        ) @connection(key: "GuildNetworksSection_guild_networks") {
          edges {
            node {
              ...GuildCard_guild
            }
          }
        }
      }
    `,
    guild
  )

  const breakpoint = useBreakpoint()

  if (data.networks.edges.length === 0) {
    return null
  }

  return (
    <Stack gap={4}>
      <Stack direction='horizontal' gap={4} alignItems='center'>
        <Text size={4} variant='cardHeading'>
          Guild Networks
        </Text>

        <HorizontalLine variant='cardHeading' />
      </Stack>

      <CardGrid
        items={data.networks.edges}
        renderItem={(edge) => (
          <GuildCard
            guild={edge.node}
            limitDescriptionLines={8}
            expand={breakpoint !== 0}
          />
        )}
        breakpointItemsPerRow={INITIAL_COUNTS}
      />

      {hasNext && (
        <Button
          variant='secondary'
          hoverVariant='secondaryHover'
          onPress={() => {
            if (isLoadingNext) {
              return
            }

            loadNext(3)
          }}
        >
          <Text variant='buttonSecondary' size={4}>
            {isLoadingNext ? 'Loading...' : 'More'}
          </Text>
        </Button>
      )}
    </Stack>
  )
}
